import { useEffect, useState } from 'react';

type Listener = (reg: ServiceWorkerRegistration) => void;
const waitingListeners = new Set<Listener>();
let waitingReg: ServiceWorkerRegistration | null = null;
let applyingUpdate = false;
let shellUpdated = false;

/** Registers /sw.js (all envs — push and offline are testable in dev). Safe to call many times. */
export function registerServiceWorker(onNavigate?: (url: string) => void) {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  if (import.meta.env.MODE === 'test') return;
  const start = () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .then((reg) => {
        const trackWaiting = () => {
          if (reg.waiting && navigator.serviceWorker.controller) {
            waitingReg = reg;
            waitingListeners.forEach((l) => l(reg));
          }
        };
        trackWaiting();
        reg.addEventListener('updatefound', () => {
          const nw = reg.installing;
          nw?.addEventListener('statechange', () => nw.state === 'installed' && trackWaiting());
        });
        // check for updates when the app comes back to the foreground
        document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && reg.update().catch(() => undefined));
      })
      .catch(() => undefined);
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // Only reload when the user accepted an update; the first install claims clients without a reload.
      if (refreshing || !applyingUpdate) return;
      refreshing = true;
      window.location.reload();
    });
    navigator.serviceWorker.addEventListener('message', (e: MessageEvent<{ type?: string; url?: string }>) => {
      if (e.data?.type === 'NAVIGATE' && e.data.url) onNavigate?.(e.data.url);
      if (e.data?.type === 'SHELL_UPDATED') {
        // a stale shell was served and a newer one is now cached: offer a reload
        shellUpdated = true;
        waitingListeners.forEach((l) => l(waitingReg ?? ({} as ServiceWorkerRegistration)));
      }
    });
  };
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
}

/** True when a new SW is installed and waiting; `apply()` activates it (page reloads on controllerchange). */
export function useServiceWorkerUpdate() {
  const [waiting, setWaiting] = useState<ServiceWorkerRegistration | null>(waitingReg);
  useEffect(() => {
    const l: Listener = (r) => setWaiting(r);
    waitingListeners.add(l);
    return () => {
      waitingListeners.delete(l);
    };
  }, []);
  return {
    available: !!waiting || shellUpdated,
    apply: () => {
      applyingUpdate = true;
      if (waiting?.waiting) waiting.waiting.postMessage({ type: 'SKIP_WAITING' });
      else window.location.reload();
    },
  };
}

/** Android/desktop install prompt capture (beforeinstallprompt). */
type BIPEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };
let deferred: BIPEvent | null = null;
const bipListeners = new Set<() => void>();
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BIPEvent;
    bipListeners.forEach((l) => l());
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    localStorage.setItem('mos.installed', '1');
    bipListeners.forEach((l) => l());
  });
}
export function useInstallPrompt() {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    bipListeners.add(l);
    return () => {
      bipListeners.delete(l);
    };
  }, []);
  return {
    canPrompt: !!deferred,
    prompt: async () => {
      if (!deferred) return 'unavailable' as const;
      await deferred.prompt();
      const { outcome } = await deferred.userChoice;
      deferred = null;
      return outcome;
    },
  };
}
